import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCreditCard,
  FiDollarSign,
  FiRefreshCw,
  FiMessageSquare,
  FiPaperclip,
  FiX,
  FiShield,
  FiCheckCircle,
  FiClock,
  FiUpload,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import {
  subscriptionPurchaseAPI,
  SubscriptionPaymentMethodId,
  SubscriptionPurchaseRequest,
  SubscriptionPurchaseSummary,
} from '../services/api';
import { getSubscriptionPurchaseAttachmentUrl } from '../config/api';
import { notifySubscriptionPurchaseTicketsUpdated } from '../utils/adminSubscriptionPurchaseEvents';
import '../components/MockCreditPanel.css';
import './SubscriptionPurchase.css';
import '../components/Dashboard.css';
import './PageTemplate.css';

interface SubscriptionPurchasePageProps {
  onLogout?: () => void;
  userEmail?: string;
}

const PAYMENT_ICONS: Record<SubscriptionPaymentMethodId, IconType> = {
  card_to_card: FiCreditCard,
  direct_rial: FiDollarSign,
  direct_usd: FiDollarSign,
  direct_crypto: FiShield,
};

const PAYMENT_DESCRIPTIONS: Record<SubscriptionPaymentMethodId, string> = {
  card_to_card: 'Transfer the exact amount to our bank card, then upload your receipt.',
  direct_rial: 'Instant payment in Iranian Rial via payment gateway.',
  direct_usd: 'Pay with international card or PayPal.',
  direct_crypto: 'Pay with Bitcoin, USDT, or other supported cryptocurrencies.',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatIrr(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function statusLabel(req: SubscriptionPurchaseRequest): string {
  if (req.status === 'open' && req.awaiting_user) return 'Action required';
  if (req.status === 'open') return 'Under review';
  return req.status.charAt(0).toUpperCase() + req.status.slice(1);
}

const SubscriptionPurchasePage: React.FC<SubscriptionPurchasePageProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [summary, setSummary] = useState<SubscriptionPurchaseSummary | null>(null);
  const [requests, setRequests] = useState<SubscriptionPurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SubscriptionPurchaseRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<SubscriptionPaymentMethodId>('card_to_card');
  const [createForm, setCreateForm] = useState({
    contact_phone: '',
    contact_email: '',
    initial_message: '',
    file: null as File | null,
  });
  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);

  const plan = summary?.plan;
  const paymentMethods = plan?.payment_methods ?? [];

  const enabledMethod = useMemo(() => {
    const enabled = paymentMethods.find((m) => m.enabled);
    return enabled?.id ?? 'card_to_card';
  }, [paymentMethods]);

  useEffect(() => {
    if (enabledMethod) setSelectedMethod(enabledMethod);
  }, [enabledMethod]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sum, reqs] = await Promise.all([
        subscriptionPurchaseAPI.getSummary(),
        subscriptionPurchaseAPI.listRequests(),
      ]);
      setSummary(sum);
      setRequests(reqs);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription purchase data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openTicket = async (id: number) => {
    try {
      setError(null);
      const detail = await subscriptionPurchaseAPI.getRequest(id);
      setSelectedRequest(detail);
      setReplyBody('');
      setReplyFile(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  };

  const handleCreate = async () => {
    if (!createForm.file) {
      setError('Please upload your payment receipt');
      return;
    }
    const method = paymentMethods.find((m) => m.id === selectedMethod);
    if (!method?.enabled) {
      setError('Selected payment method is not available');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await subscriptionPurchaseAPI.createRequest({
        payment_method: selectedMethod,
        contact_phone: createForm.contact_phone,
        contact_email: createForm.contact_email,
        initial_message: createForm.initial_message,
        file: createForm.file,
      });
      setShowCreateModal(false);
      setCreateForm({ contact_phone: '', contact_email: '', initial_message: '', file: null });
      await loadData();
      notifySubscriptionPurchaseTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!selectedRequest) return;
    if (!replyBody.trim() && !replyFile) {
      setError('Enter a message or attach a file');
      return;
    }
    try {
      setSubmitting(true);
      const updated = await subscriptionPurchaseAPI.addMessage(selectedRequest.id, replyBody, replyFile);
      setSelectedRequest(updated);
      setReplyBody('');
      setReplyFile(null);
      await loadData();
      notifySubscriptionPurchaseTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedRequest) return;
    try {
      setSubmitting(true);
      const updated = await subscriptionPurchaseAPI.cancelRequest(selectedRequest.id);
      setSelectedRequest(updated);
      await loadData();
      notifySubscriptionPurchaseTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  const renderAttachment = (reqId: number, att: { id: number; file_name: string; mime_type: string }) => {
    const url = getSubscriptionPurchaseAttachmentUrl(reqId, att.id);
    const isImage = att.mime_type.startsWith('image/');
    return (
      <div key={att.id} className="mock-credits-attachments">
        {url && isImage ? (
          <img src={url} alt={att.file_name} className="mock-credits-attachment-preview" />
        ) : null}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="mock-credits-attachment-link">
            {React.createElement(FiPaperclip as React.ComponentType<{ size?: number }>, { size: 14 })}
            {att.file_name}
          </a>
        ) : null}
      </div>
    );
  };

  const hasActiveSub = summary?.has_subscription;
  const sub = summary?.subscription;

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader userEmail={userEmail || 'User'} organization="NAATI" onLogout={onLogout} />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div>
                <h1 className="dashboard-title">Get Subscription</h1>
                <p className="dashboard-subtitle">
                  Unlock premium practice materials. Choose a payment method, transfer the fixed amount, upload your receipt, and wait for admin confirmation.
                </p>
              </div>
            </motion.div>

            <div className="sub-purchase-page">
            <div className="sub-purchase-hero">
              <div className={`sub-purchase-status-card ${hasActiveSub ? '' : 'inactive'}`}>
                <p className="sub-purchase-card-label">Your subscription</p>
                {loading ? (
                  <p className="sub-purchase-status-value">…</p>
                ) : hasActiveSub && sub ? (
                  <>
                    <p className="sub-purchase-status-value">
                      {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 22 })}
                      {' '}
                      Active
                    </p>
                    <p className="sub-purchase-status-meta">
                      Expires {formatDate(sub.expires_at)} · {sub.days_remaining} day(s) remaining
                    </p>
                  </>
                ) : (
                  <>
                    <p className="sub-purchase-status-value">No active subscription</p>
                    <p className="sub-purchase-status-meta">Purchase below to access premium content</p>
                  </>
                )}
              </div>

              <div className="sub-purchase-price-card">
                <p className="sub-purchase-card-label">Subscription price</p>
                {plan ? (
                  <>
                    <div className="sub-purchase-price-row">
                      <span className="sub-purchase-price-usd">${Number(plan.price_usd).toFixed(2)}</span>
                      <span className="sub-purchase-price-irr">{formatIrr(plan.price_irr)} IRR</span>
                    </div>
                    <p className="sub-purchase-duration">{plan.duration_days} days of full access</p>
                  </>
                ) : (
                  <p className="sub-purchase-status-value">…</p>
                )}
              </div>
            </div>

            <h2 className="sub-purchase-section-title">Payment method</h2>
            <div className="sub-purchase-methods">
              {paymentMethods.map((method) => {
                const Icon = PAYMENT_ICONS[method.id];
                const isSelected = selectedMethod === method.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    className={`sub-purchase-method ${isSelected ? 'selected' : ''}`}
                    disabled={!method.enabled}
                    onClick={() => method.enabled && setSelectedMethod(method.id)}
                  >
                    <span className={`sub-purchase-method-badge ${method.enabled ? '' : 'soon'}`}>
                      {method.enabled ? 'Available' : 'Coming soon'}
                    </span>
                    <span className="sub-purchase-method-icon">
                      {React.createElement(Icon as React.ComponentType<{ size?: number }>, { size: 20 })}
                    </span>
                    <span className="sub-purchase-method-title">{method.label}</span>
                    <span className="sub-purchase-method-desc">{PAYMENT_DESCRIPTIONS[method.id]}</span>
                  </button>
                );
              })}
            </div>

            {selectedMethod === 'card_to_card' && plan?.card_to_card_instructions ? (
              <div className="sub-purchase-instructions">
                <p className="sub-purchase-instructions-title">Card-to-card instructions</p>
                {plan.card_to_card_instructions}
              </div>
            ) : null}

            <div className="sub-purchase-cta-row">
              <button
                type="button"
                className="mock-credits-btn mock-credits-btn-primary sub-purchase-btn-primary"
                onClick={() => setShowCreateModal(true)}
                disabled={loading || !paymentMethods.some((m) => m.enabled)}
              >
                {React.createElement(FiUpload as React.ComponentType<{ size?: number }>, { size: 16 })}
                Submit payment receipt
              </button>
              <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={loadData} disabled={loading}>
                {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, {
                  size: 16,
                  className: loading ? 'spinner' : undefined,
                })}
                Refresh
              </button>
            </div>

            {error ? <div className="mock-credits-alert mock-credits-alert-warn">{error}</div> : null}

            {summary && summary.open_requests_count > 0 ? (
              <div className="mock-credits-alert mock-credits-alert-info">
                {React.createElement(FiClock as React.ComponentType<{ size?: number }>, { size: 16 })}
                {' '}
                You have {summary.open_requests_count} open request(s) awaiting review.
              </div>
            ) : null}

            <section className="sub-purchase-panel">
              <h3 className="mock-credits-tickets-title">Your purchase requests</h3>
              {loading ? (
                <p className="mock-credits-tickets-empty">Loading…</p>
              ) : requests.length === 0 ? (
                <p className="mock-credits-tickets-empty">
                  No requests yet. Submit your receipt after payment to open a ticket.
                </p>
              ) : (
                <div className="mock-credits-ticket-list">
                  {requests.map((req) => (
                    <button
                      key={req.id}
                      type="button"
                      className="mock-credits-ticket-row"
                      onClick={() => openTicket(req.id)}
                    >
                      <div>
                        <span className="mock-credits-ticket-id">Ticket #{req.id}</span>
                        <span className="mock-credits-ticket-meta">
                          {req.payment_method_label} · ${req.price_usd_snapshot} / {formatIrr(req.price_irr_snapshot)} IRR
                        </span>
                      </div>
                      <span className={`mock-credits-status sub-purchase-status-${req.status}`}>
                        {statusLabel(req)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showCreateModal ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setShowCreateModal(false)}
          >
            <motion.div
              className="mock-credits-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>Submit payment receipt</h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setShowCreateModal(false)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  Method: <strong>{paymentMethods.find((m) => m.id === selectedMethod)?.label}</strong>
                  {plan ? (
                    <>
                      {' '}
                      · Amount: <strong>${Number(plan.price_usd).toFixed(2)}</strong> or{' '}
                      <strong>{formatIrr(plan.price_irr)} IRR</strong>
                    </>
                  ) : null}
                </p>
                <div className="mock-credits-form-row">
                  <div className="mock-credits-form-group">
                    <label>Phone (optional)</label>
                    <input
                      type="tel"
                      value={createForm.contact_phone}
                      onChange={(e) => setCreateForm((f) => ({ ...f, contact_phone: e.target.value }))}
                    />
                  </div>
                  <div className="mock-credits-form-group">
                    <label>Email (optional)</label>
                    <input
                      type="email"
                      value={createForm.contact_email}
                      onChange={(e) => setCreateForm((f) => ({ ...f, contact_email: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mock-credits-form-group">
                  <label>Message (optional)</label>
                  <textarea
                    value={createForm.initial_message}
                    onChange={(e) => setCreateForm((f) => ({ ...f, initial_message: e.target.value }))}
                    placeholder="e.g. Transaction reference, payer name…"
                  />
                </div>
                <div className="mock-credits-form-group">
                  <label>Payment receipt *</label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setCreateForm((f) => ({ ...f, file: e.target.files?.[0] ?? null }))}
                  />
                </div>
              </div>
              <div className="mock-credits-modal-footer">
                <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="mock-credits-btn mock-credits-btn-primary sub-purchase-btn-primary"
                  onClick={handleCreate}
                  disabled={submitting || !createForm.file}
                >
                  Submit & open ticket
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {selectedRequest ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setSelectedRequest(null)}
          >
            <motion.div
              className="mock-credits-modal mock-credits-modal-wide"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>
                  {React.createElement(FiMessageSquare as React.ComponentType<{ size?: number }>, { size: 20 })}
                  {' '}
                  Ticket #{selectedRequest.id}
                </h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setSelectedRequest(null)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  {selectedRequest.payment_method_label} · ${selectedRequest.price_usd_snapshot} ·{' '}
                  {formatIrr(selectedRequest.price_irr_snapshot)} IRR · {selectedRequest.duration_days_snapshot} days
                </p>
                {selectedRequest.status === 'open' && selectedRequest.awaiting_user ? (
                  <div className="mock-credits-alert mock-credits-alert-info" style={{ marginBottom: '1rem' }}>
                    Admin replied — please respond or upload additional proof if requested.
                  </div>
                ) : null}
                <div className="mock-credits-thread">
                  {(selectedRequest.messages || []).map((msg) => (
                    <div
                      key={msg.id}
                      className={`mock-credits-msg mock-credits-msg-${msg.author_role}`}
                    >
                      <div className="mock-credits-msg-role">{msg.author_role === 'admin' ? 'Admin' : 'You'}</div>
                      <div className="mock-credits-msg-body">{msg.body}</div>
                      <div className="mock-credits-msg-time">{formatDate(msg.created_at)}</div>
                      {msg.attachments?.map((att) => renderAttachment(selectedRequest.id, att))}
                    </div>
                  ))}
                  {(selectedRequest.attachments || [])
                    .filter((att) => !(selectedRequest.messages || []).some((m) => m.attachments?.some((a) => a.id === att.id)))
                    .map((att) => renderAttachment(selectedRequest.id, att))}
                </div>
                {selectedRequest.status === 'open' ? (
                  <div className="mock-credits-reply-box">
                    <div className="mock-credits-form-group">
                      <label>Reply to admin</label>
                      <textarea
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        placeholder="Your message…"
                      />
                    </div>
                    <div className="mock-credits-file-input-wrap">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => setReplyFile(e.target.files?.[0] ?? null)} />
                    </div>
                  </div>
                ) : (
                  <div className="mock-credits-alert mock-credits-alert-info">
                    {selectedRequest.status === 'approved' ? 'Approved' : selectedRequest.status}
                    {selectedRequest.admin_resolution_note ? `: ${selectedRequest.admin_resolution_note}` : ''}
                  </div>
                )}
              </div>
              <div className="mock-credits-modal-footer">
                {selectedRequest.status === 'open' ? (
                  <>
                    <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={handleCancel} disabled={submitting}>
                      Cancel ticket
                    </button>
                    <button type="button" className="mock-credits-btn mock-credits-btn-primary sub-purchase-btn-primary" onClick={handleReply} disabled={submitting}>
                      Send reply
                    </button>
                  </>
                ) : (
                  <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setSelectedRequest(null)}>
                    Close
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionPurchasePage;
